/**
 * AIShield Telemetry Polling Manager
 * Core polling lifecycle, sequence counter, exponential backoff, visibility handling,
 * and honest caching state machine.
 */

import type {
  BundleQueryParams,
  AdaptedDashboardBundle,
  PollingStatus,
} from "../types/telemetry.ts";
import { fetchDashboardBundle, ApiClientError } from "./apiClient.ts";
import { adaptDashboardBundle } from "./adapters.ts";

export interface PollingManagerOptions {
  initialQuery?: BundleQueryParams;
  baseIntervalMs?: number;      // default: 15,000ms (15s)
  firstBackoffMs?: number;      // default: 30,000ms (30s)
  maxBackoffMs?: number;        // default: 60,000ms (60s)
  fetchFn?: typeof fetchDashboardBundle;
  autoStart?: boolean;
  initiallyPaused?: boolean;
  onAuthExpired?: () => void;
}

export interface PollingState {
  status: PollingStatus;
  data: AdaptedDashboardBundle | null;
  error: Error | null;
  hasNewData: boolean;
  serverTime: string | null;
  queryIdentity: string;
  query: BundleQueryParams;
}

export type PollingListener = (state: PollingState) => void;

export function computeQueryIdentity(q: BundleQueryParams): string {
  return JSON.stringify({
    websiteId: q.websiteId ?? null,
    demoEndpoint: q.demoEndpoint ?? false,
    timeRange: q.timeRange ?? "60m",
    shortcut: q.shortcut ?? "all",
    decision: q.decision ?? "ALL",
    inspectionStatus: q.inspectionStatus ?? "ALL",
    search: (q.search ?? "").trim(),
    sortField: q.sortField ?? "timestamp",
    sortOrder: q.sortOrder ?? "desc",
    page: q.page ?? 1,
    pageSize: q.pageSize ?? 10,
  });
}

export class TelemetryPollingManager {
  private query: BundleQueryParams;
  private queryIdentity: string;
  private status: PollingStatus = "idle";
  private data: AdaptedDashboardBundle | null = null;
  private pendingData: AdaptedDashboardBundle | null = null;
  private error: Error | null = null;
  private hasNewData: boolean = false;
  private serverTime: string | null = null;

  private activeSeq: number = 0;
  private consecutiveFailures: number = 0;
  private isDestroyed: boolean = false;
  private isPaused: boolean = false;
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private activeAbortController: AbortController | null = null;
  private isFetching: boolean = false;

  private readonly baseIntervalMs: number;
  private readonly firstBackoffMs: number;
  private readonly maxBackoffMs: number;
  private readonly fetchFn: typeof fetchDashboardBundle;
  private readonly listeners = new Set<PollingListener>();
  private readonly onAuthExpired?: () => void;

  constructor(options: PollingManagerOptions = {}) {
    this.query = options.initialQuery ?? {};
    this.queryIdentity = computeQueryIdentity(this.query);
    this.baseIntervalMs = options.baseIntervalMs ?? 15000;
    this.firstBackoffMs = options.firstBackoffMs ?? 30000;
    this.maxBackoffMs = options.maxBackoffMs ?? 60000;
    this.isPaused = options.initiallyPaused ?? false;
    this.fetchFn = options.fetchFn ?? fetchDashboardBundle;
    this.onAuthExpired = options.onAuthExpired;

    if (options.autoStart ?? true) {
      this.start();
    }
  }

  public getState(): PollingState {
    return {
      status: this.status,
      data: this.data,
      error: this.error,
      hasNewData: this.hasNewData,
      serverTime: this.serverTime,
      queryIdentity: this.queryIdentity,
      query: this.query,
    };
  }

  public getQuery(): BundleQueryParams {
    return { ...this.query };
  }

  public getNextIntervalMs(): number {
    if (this.consecutiveFailures === 1) return this.firstBackoffMs;
    if (this.consecutiveFailures >= 2) return this.maxBackoffMs;
    return this.baseIntervalMs;
  }

  public subscribe(listener: PollingListener): () => void {
    this.listeners.add(listener);
    listener(this.getState());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    if (this.isDestroyed) return;
    const state = this.getState();
    this.listeners.forEach((listener) => {
      try {
        listener(state);
      } catch (err) {
        console.error("PollingManager listener error:", err);
      }
    });
  }

  public start() {
    if (this.isDestroyed) return;
    this.triggerFetch({ isManual: false });
  }

  public stop() {
    this.clearTimer();
    this.abortInFlight();
  }

  public destroy() {
    this.isDestroyed = true;
    this.stop();
    this.listeners.clear();
  }

  public pause() {
    this.isPaused = true;
    this.clearTimer();
  }

  public resume() {
    if (!this.isPaused || this.isDestroyed) return;
    this.isPaused = false;
    if (!this.isFetching) {
      this.triggerFetch({ isManual: false });
    }
  }

  public async refresh(): Promise<boolean> {
    if (this.isDestroyed) return false;
    this.clearTimer();
    return await this.triggerFetch({ isManual: true });
  }

  public setQuery(newQuery: Partial<BundleQueryParams>) {
    if (this.isDestroyed) return;

    const filtersOrRangeChanged =
      (newQuery.timeRange !== undefined && newQuery.timeRange !== this.query.timeRange) ||
      (newQuery.shortcut !== undefined && newQuery.shortcut !== this.query.shortcut) ||
      (newQuery.decision !== undefined && newQuery.decision !== this.query.decision) ||
      (newQuery.inspectionStatus !== undefined && newQuery.inspectionStatus !== this.query.inspectionStatus) ||
      (newQuery.search !== undefined && newQuery.search !== this.query.search);

    const mergedQuery: BundleQueryParams = {
      ...this.query,
      ...newQuery,
    };

    if (filtersOrRangeChanged && newQuery.page === undefined) {
      mergedQuery.page = 1;
    }

    const newIdentity = computeQueryIdentity(mergedQuery);
    if (newIdentity === this.queryIdentity) {
      return;
    }

    // Abort obsolete in-flight request for previous query
    this.abortInFlight();
    this.clearTimer();

    this.query = mergedQuery;
    this.queryIdentity = newIdentity;
    this.hasNewData = false;
    this.pendingData = null;

    // Reset data for changed query so old results are not shown as new query's results
    this.data = null;
    this.serverTime = null;
    this.error = null;
    this.status = "loading";
    this.hasNewData = false;
    this.pendingData = null;
    this.consecutiveFailures = 0;
    this.notify();

    this.triggerFetch({ isManual: false });
  }

  public applyPendingData() {
    if (this.pendingData) {
      this.data = this.pendingData;
      this.serverTime = this.pendingData.serverTime;
      this.pendingData = null;
      this.hasNewData = false;

      // Applying cached pending data after a newer failed fetch must not falsely imply connectivity has recovered.
      if (this.status !== "stale" && this.status !== "error") {
        const isEmpty = this.data.summary.totalRequests === 0 && this.data.table.total === 0;
        this.status = isEmpty ? "empty" : "ready";
      }
      this.notify();
    }
  }

  private clearTimer() {
    if (this.timerId !== null) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  private abortInFlight() {
    if (this.activeAbortController) {
      this.activeAbortController.abort();
      this.activeAbortController = null;
    }
    this.isFetching = false;
  }

  private scheduleNextPoll() {
    if (this.isDestroyed || this.isPaused) return;
    this.clearTimer();

    let delayMs = this.baseIntervalMs;
    if (this.consecutiveFailures === 1) {
      delayMs = this.firstBackoffMs;
    } else if (this.consecutiveFailures >= 2) {
      delayMs = this.maxBackoffMs;
    }

    const timer = setTimeout(() => {
      this.triggerFetch({ isManual: false });
    }, delayMs);

    if (typeof timer === "object" && timer && "unref" in timer && typeof (timer as any).unref === "function") {
      (timer as any).unref();
    }

    this.timerId = timer;
  }

  private async triggerFetch(_options: { isManual?: boolean } = {}): Promise<boolean> {
    if (this.isDestroyed) return false;

    this.clearTimer();
    this.abortInFlight();

    const isManual = Boolean(_options?.isManual);
    const seq = ++this.activeSeq;
    const currentIdentity = this.queryIdentity;
    const controller = new AbortController();
    this.activeAbortController = controller;
    this.isFetching = true;

    // State transition before fetch
    if (this.data !== null) {
      this.status = "refreshing";
    } else {
      this.status = "loading";
    }
    this.notify();

    try {
      const raw = await this.fetchFn(this.query, {
        signal: controller.signal,
      });

      // Discard response if obsolete sequence or changed query
      if (seq !== this.activeSeq || currentIdentity !== this.queryIdentity || this.isDestroyed) {
        return false;
      }

      const adapted = adaptDashboardBundle(raw);
      this.consecutiveFailures = 0;
      this.error = null;

      const isEmpty = adapted.summary.totalRequests === 0 && adapted.table.total === 0;
      const currentPage = this.query.page ?? 1;

      // Background poll on later page: retain current rows if rows differ, expose pending data
      if (currentPage > 1 && this.data !== null && !isManual) {
        const currentItemIds = this.data.table.items.map((i) => i.id).join(",");
        const newItemIds = adapted.table.items.map((i) => i.id).join(",");
        if (currentItemIds !== newItemIds || this.data.table.total !== adapted.table.total) {
          this.hasNewData = true;
          this.pendingData = adapted;
          this.status = isEmpty ? "empty" : "ready";
          this.notify();
          this.isFetching = false;
          this.scheduleNextPoll();
          return true;
        }
      }

      // Manual refresh or page 1 or matching rows: apply immediately
      this.data = adapted;
      this.serverTime = adapted.serverTime;
      this.hasNewData = false;
      this.pendingData = null;
      this.status = isEmpty ? "empty" : "ready";
      this.notify();
      return true;
    } catch (err: unknown) {
      if (seq !== this.activeSeq || currentIdentity !== this.queryIdentity || this.isDestroyed) {
        return false;
      }

      // Check if aborted deliberately
      if (err instanceof Error && err.name === "AbortError") {
        return false;
      }

      this.consecutiveFailures++;
      const errorObj = err instanceof Error ? err : new Error(String(err));
      this.error = errorObj;

      if (err instanceof ApiClientError && err.status === 401) {
        this.status = "error";
        this.data = null;
        this.serverTime = null;
        this.notify();
        this.onAuthExpired?.();
        return false;
      }

      if (this.data !== null) {
        // Same query refresh failure: retain cached data, marked as stale
        this.status = "stale";
      } else {
        // Initial failure or failed changed-query: expose unavailable/error state
        this.status = "error";
        this.data = null;
        this.serverTime = null;
      }
      this.notify();
      return false;
    } finally {
      if (seq === this.activeSeq) {
        this.isFetching = false;
        this.activeAbortController = null;
        this.scheduleNextPoll();
      }
    }
  }
}
